import { useState } from 'react';
import { useLocale, useT } from '../i18n/locale-context';
import { LocaleLink } from '../i18n/locale-link';
import { formatBudgetCompact, formatTashkentDate } from '../lib/format';
import { isLinkableTin } from '../lib/tin';
import { useIsBelow } from '../lib/viewport';
import { cn } from '../lib/utils';
import { splitOrgForm } from './buyer-graph-panel';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from './ui/sheet';

/**
 * The pieces the two company lists on the lot card share — «Вероятные
 * участники» and the buyer's supplier concentration — since both got
 * «Показать всех (N)» on the same day (owner, 2026-10-18): the «торги /
 * напрямую» chip, the newest-contract date and the full list.
 *
 * The full list is a SHEET over the page, never inline: up to 200 rows do not
 * belong on the card (owner, 18.10 ~11:30). The same `ui/sheet` the catalog's
 * mobile filters use — from the bottom on a phone (swipe down to close), from
 * the right on a desktop; Esc, the ✕ and a tap outside close it everywhere. A
 * «найти компанию» field filters the rows on the client. Newest contract first,
 * the only order (the server's).
 */

export type CompanyKind = 'tender' | 'direct' | 'mixed';

/** One company row of a full list. `kind` or `sharePct` closes the line. */
export interface CompanyListRow {
  tin: string;
  name: string | null;
  lastAwardAt: string | null | undefined;
  lastAwardApprox: boolean | undefined;
  contracts: number;
  totalUzs: number;
  kind?: CompanyKind;
  sharePct?: number;
}

/** «торги» / «прямой договор» / «торги и прямой договор» (owner 19.10: «напрямую» read as «arranged»). */
export function KindChip({ kind }: { kind: CompanyKind }) {
  const t = useT();
  const tone =
    kind === 'tender'
      ? 'border-edge-light bg-bone text-ink-muted'
      : 'border-rust/30 bg-orange/10 text-rust';
  return (
    <span
      className={`shrink-0 whitespace-nowrap rounded border px-1 py-px text-[10px] font-semibold ${tone}`}
    >
      {t(`companyList.kind.${kind}`)}
    </span>
  );
}

/** The newest contract's date, «≈» and an explanation when it is a bid
 *  deadline standing in for an award date the source does not publish. */
export function LastAwardDate({
  iso,
  approx,
}: {
  iso: string | null | undefined;
  approx?: boolean;
}) {
  const t = useT();
  const date = formatTashkentDate(iso);
  if (!date) return <>—</>;
  return approx ? <span title={t('companyList.approxDate')}>≈ {date}</span> : <>{date}</>;
}

/** Does a row match the «найти компанию» text — name or ИНН, any case. */
export function matchesCompany(row: Pick<CompanyListRow, 'tin' | 'name'>, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (row.name ?? '').toLowerCase().includes(q) || row.tin.includes(q);
}

/**
 * «Показать всех (N)»: the sheet. `rows` is null while the list loads; `total`
 * is the true count (the title's N), and when the server capped the list a
 * line says how many of how many are shown.
 */
export function CompanyListSheet({
  open,
  onOpenChange,
  title,
  description,
  total,
  rows,
  error,
  contractsLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** For screen readers — the card's subtitle or hint. */
  description: string;
  total: number;
  rows: CompanyListRow[] | null;
  error: boolean;
  contractsLabel: (n: number) => string;
}) {
  const t = useT();
  const { locale } = useLocale();
  const isMobile = useIsBelow('md');
  const [query, setQuery] = useState('');
  const shown = rows ? rows.filter((r) => matchesCompany(r, query)) : null;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setQuery('');
        onOpenChange(next);
      }}
    >
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        /*
         * The panel is the sheet's ONE scroller, header sticky inside it — as in
         * the catalog filters: `ui/sheet`'s swipe-to-dismiss arms when the
         * PANEL's `scrollTop` is 0, so an inner scroller would let a downward
         * drag on a half-scrolled list close the sheet instead of scrolling it.
         */
        className={cn(
          'overflow-y-auto overscroll-contain bg-bone-surface p-0',
          isMobile
            ? 'max-h-[88vh] rounded-t-2xl border-t border-edge supports-[height:1dvh]:max-h-[88dvh]'
            : 'border-l border-edge sm:max-w-[520px]',
        )}
      >
        <div className="sticky top-0 z-10 border-b border-edge bg-bone-surface px-5 pb-3 pt-4">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="text-[16px] font-bold text-ink">
              {title} ({total})
            </SheetTitle>
            <SheetClose
              aria-label={t('companyList.close')}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[18px] text-ink-muted hover:bg-bone hover:text-ink"
            >
              ✕
            </SheetClose>
          </div>
          <SheetDescription className="sr-only">{description}</SheetDescription>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('companyList.search')}
            aria-label={t('companyList.search')}
            className="mt-3 h-10 w-full rounded-md border border-edge bg-bone px-3 text-[14px] text-ink placeholder:text-ink-muted"
          />
        </div>

        <div className="px-5 pb-5 pt-1">
          {shown === null ? (
            <p className="py-3 text-[13px] text-ink-muted">
              {error ? t('companyList.error') : t('companyList.loading')}
            </p>
          ) : shown.length === 0 ? (
            <p className="py-3 text-[13px] text-ink-muted">{t('companyList.notFound')}</p>
          ) : (
            <ul className="flex flex-col">
              {shown.map((r) => (
                <CompanyRow key={r.tin} row={r} locale={locale} contractsLabel={contractsLabel} />
              ))}
            </ul>
          )}
          {rows && total > rows.length && (
            <p className="mt-3 text-[11.5px] text-ink-muted">
              {t('companyList.truncated', { n: rows.length, total })}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Name (link) · chip on the first line; «последний 14.10.2026 · 3 контракта ·
 *  1,2 млрд сум» (and the share) under it. */
function CompanyRow({
  row,
  locale,
  contractsLabel,
}: {
  row: CompanyListRow;
  locale: string;
  contractsLabel: (n: number) => string;
}) {
  const t = useT();
  const { name } = splitOrgForm(row.name ?? row.tin);
  const sum =
    row.totalUzs > 0
      ? `${formatBudgetCompact(String(Math.round(row.totalUzs)), locale)} ${t('tender.budget.unit')}`
      : null;
  return (
    <li className="border-t border-edge-light py-2.5 first:border-t-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {/* Only a 9-digit ИНН has a profile page — see isLinkableTin. */}
        {isLinkableTin(row.tin) ? (
          <LocaleLink
            to={`/supplier/${row.tin}`}
            title={row.name ?? row.tin}
            className="truncate text-[13.5px] font-semibold text-brand hover:underline"
          >
            {name}
          </LocaleLink>
        ) : (
          <span title={row.name ?? row.tin} className="truncate text-[13.5px] font-semibold">
            {name}
          </span>
        )}
        {row.kind && <KindChip kind={row.kind} />}
      </div>
      <div className="mt-0.5 text-[12px] tabular-nums text-ink-muted">
        {t('companyList.last')} <LastAwardDate iso={row.lastAwardAt} approx={row.lastAwardApprox} />
        {' · '}
        <span className="font-semibold text-ink">{contractsLabel(row.contracts)}</span>
        {sum && ` · ${sum}`}
        {row.sharePct != null && ` · ${t('companyList.share', { pct: row.sharePct.toFixed(1) })}`}
      </div>
    </li>
  );
}
